/** @jsxImportSource @emotion/react */
"use client";
import { I18nMessage } from "@i18n/react";
import { forwardRef, type JSX } from "react";

import type { ThemeType } from "../../../../../global/emotion";
import { spacing } from "../../../../../global/emotion";
import { useIsDesktopViewport } from "../../../../hooks/useIsDesktopViewport";
import type { HallowElement } from "../../../../types/hallowElement.types";
import type { HallowElementProps } from "../../../../types/hallowElementProps.types";
import type { KeyedElement } from "../../../../types/keyedElement.types";
import type { WithAsChild } from "../../../../types/withAsChild.types";
import { determineChildrenInject } from "../../../../utils/determineChildrenInject";
import { determineElementFromAsChild } from "../../../../utils/determineElementFromAsChild";
import type { AvatarProps } from "../Avatar/Avatar.emotion";
import { Avatar } from "../Avatar/Avatar.emotion";
import { Text } from "../Text/Text.emotion";

const hallowElement: HallowElement = "div";

const createdByStyle = (theme: ThemeType) => ({
  color: theme.semanticColors.neutralsMedium,
});
const determinedElementStyle = {
  alignItems: "center",
  display: "flex",
  gap: spacing.ms,
  width: spacing.full,
};
const infoStyle = {
  display: "flex",
  flexDirection: "column" as "column",
  flexGrow: 1,
  gap: spacing.t,
  minWidth: spacing.none,
};

export type AuthorProps = WithAsChild<
  HallowElementProps<typeof hallowElement>
> & {
  avatarProps?: AvatarProps;
  name?: string;
  rightActions?: KeyedElement | KeyedElement[];
  labelMessage?: string;
};

export const Author = forwardRef<any, AuthorProps>(
  (
    {
      asChild = false,
      avatarProps,
      children,
      name,
      rightActions,
      labelMessage = "web_created_by",
      ...props
    },
    ref,
  ) => {
    const isDesktop = useIsDesktopViewport();

    const DeterminedElement = determineElementFromAsChild({
      asChild,
      hallowElement,
    });

    const determinedChildren = determineChildrenInject({
      afterChildrenInject: [
        <Avatar key="avatar" size={isDesktop ? 64 : 40} {...avatarProps} />,
        <div key="info" css={infoStyle}>
          <Text
            overflow="ellipsis"
            size={"l"}
            type="detail"
            css={createdByStyle}
          >
            <I18nMessage message={labelMessage} />
          </Text>
          <Text overflow="ellipsis" size={"s"} type="title">
            {name}
          </Text>
        </div>,
        rightActions as JSX.Element,
      ],
      children: children as JSX.Element,
    });

    return (
      <DeterminedElement ref={ref} css={determinedElementStyle} {...props}>
        {determinedChildren}
      </DeterminedElement>
    );
  },
);

Author.displayName = "Author";
